import React, { ReactNode, Suspense, useEffect, useState, useRef } from 'react'
import cx from 'classnames'
import { SidebarType } from 'configs/constants/enums'
import { useStores } from 'hooks/useStores'
import { observer } from 'mobx-react'
import { Container } from '@chakra-ui/react'
import styles from './verticalLayout.module.scss'
import GlobalSpinner from 'components/GlobalSpinner'
import LeftSidebar from 'components/LeftSidebar'
import Topbar from 'components/Topbar'
import NotificationPermissionBanner from 'components/NotificationPermissionBanner'
import { Outlet } from 'react-router-dom'
import WorkEmailVerificationFlow from 'components/EmailVerificationFlow/WorkEmailVerificationFlow'

interface IVerticalLayoutProps {
  children?: ReactNode
}

const VerticalLayout = (props: IVerticalLayoutProps) => {
  const { children } = props
  const { generalStore } = useStores()
  const { showSidebarMobile } = generalStore
  const [sidebarType, setSidebarType] = useState<SidebarType | ''>(SidebarType.FIXED)
  const ref = useRef<HTMLDivElement>(null)
  const isCondensed = sidebarType === SidebarType.CONDENSED

  function updateDimensions() {
    //* activate the condensed sidebar if smaller devices like ipad or tablet
    if (window.innerWidth >= 768 && window.innerWidth <= 1028) {
      changeSidebarTypeUI(SidebarType.CONDENSED)
    } else if (window.innerWidth > 1028) {
      changeSidebarTypeUI(SidebarType.FIXED)
    } else if (window.innerWidth <= 768) {
      changeSidebarTypeUI('')
    }
  }

  function changeSidebarTypeUI(newType: SidebarType | ''): void {
    setSidebarType(newType)
    document.body.setAttribute('data-leftbar-compact-mode', newType)
  }

  function toggleSidebarCollapse() {
    changeSidebarTypeUI(sidebarType === SidebarType.CONDENSED ? SidebarType.FIXED : SidebarType.CONDENSED)
  }

  useEffect(() => {
    window.addEventListener('resize', updateDimensions)

    if (document.body) document.body.setAttribute('data-leftbar-theme', 'dark')

    //* activate the condensed sidebar if smaller devices like ipad or tablet
    if (window.innerWidth >= 768 && window.innerWidth <= 1028) {
      changeSidebarTypeUI(SidebarType.CONDENSED)
    } else if (window.innerWidth <= 768) {
      changeSidebarTypeUI('')
    }

    return () => {
      window.removeEventListener('resize', updateDimensions)
    }
  }, [])

  return (
    <div className="app">
      <div className={cx(styles.wrapper, 'wrapper')}>
        <div ref={ref} className={styles.sidebarContainer}>
          <Suspense fallback={<GlobalSpinner />}>
            <LeftSidebar
              {...props}
              isCondensed={isCondensed}
              showSidebarMobile={showSidebarMobile}
              setShowSidebarMobile={showSidebarMobile => generalStore.setShowSidebarMobile(showSidebarMobile)}
            />
          </Suspense>
        </div>

        <div className={cx('content-page', styles.contentPageWithTransition)}>
          <div className="content">
            <Suspense fallback={<GlobalSpinner />}>
              <Topbar
                hideLogo={true}
                toggleSidebarCollapse={toggleSidebarCollapse}
                setShowSidebarMobile={() => generalStore.setShowSidebarMobile(!showSidebarMobile)}
                showSidebarMobile={showSidebarMobile}
              />
            </Suspense>

            <Container maxW="full" px={0}>
              <NotificationPermissionBanner />
              <Suspense fallback={<GlobalSpinner />}>
                <Outlet />
                <WorkEmailVerificationFlow />
              </Suspense>
            </Container>
          </div>
        </div>
      </div>
    </div>
  )
}

export default observer(VerticalLayout)
